import { useEffect, useState } from "react";

import {
  isRecordingClaimed,
  ownsRecordingHere,
} from "@/services/recordingOwnership";

import { callApi } from "@utils/callApi";

export type RecordingBusyReason = "other-tab" | "elsewhere" | "processing" | null;

export function useRecordingBusy(caseNumber?: string): {
  busy: boolean;
  reason: RecordingBusyReason;
  /** A sent recording is still processing and a new one may replace it. */
  replaceable: boolean;
} {
  const [browserBusy, setBrowserBusy] = useState(false);
  // Keyed by case so a previous case's answer never applies to the new one.
  const [serverState, setServerState] = useState<{
    caseNumber?: string;
    busy: boolean;
    recording?: boolean;
    replaceable?: boolean;
  }>({ busy: false });
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      if (alive) setBrowserBusy(await isRecordingClaimed());
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 250);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    if (!caseNumber) return;
    let alive = true;
    const refresh = async () => {
      try {
        const state = await callApi(
          `transcription/submissions/state/${caseNumber}`,
          "GET"
        );
        if (alive)
          setServerState({
            caseNumber,
            busy: state.busy,
            recording: !!state.recording,
            replaceable: !!state.replaceable,
          });
      } catch {
        /* The mutation still checks ownership if this observation fails. */
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 3000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [caseNumber]);
  const forCase = serverState.caseNumber === caseNumber;
  const replaceable = forCase && !!serverState.replaceable;
  const serverBusy = forCase && serverState.busy && !replaceable;
  const busy = browserBusy || serverBusy;
  // This tab's own recording of this case is not a blocked state worth explaining.
  if (!busy || ownsRecordingHere(caseNumber))
    return { busy, reason: null, replaceable };
  const lockedElsewhere = browserBusy && !ownsRecordingHere();
  const reason = lockedElsewhere
    ? "other-tab"
    : !serverBusy
      ? null
      : serverState.recording
        ? "elsewhere"
        : "processing";
  return { busy, reason, replaceable };
}
